import {
  Avatar,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { buildPermission } from "portal/utils/auth";
import { ConfigValueView } from "./ConfigValueView";
import { DateTime } from "luxon";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { ConfigAuditLog as RosyAuditLog } from "protos/robot_syncer/audit";
import { skipToken } from "@reduxjs/toolkit/query";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { userApi } from "common/state/portalApi/user";
import { useTranslation } from "react-i18next";
import { withAuthorizationRequired } from "../auth/WithAuthorizationRequired";
import ChangeIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import React, { FunctionComponent } from "react";

interface ConfigAuditLogProps {
  serial: string;
  path: string;
  open?: boolean;
  onClose?: () => void;
}

const _ConfigAuditLog: FunctionComponent<ConfigAuditLogProps> = ({
  serial,
  path: key,
  open = false,
  onClose,
}) => {
  const { t } = useTranslation();

  const { data, isLoading } = useQueryPopups(
    portalApi.endpoints.getConfigAuditLog.useQuery(
      open ? { serial, key } : skipToken
    )
  );
  const logs = data?.auditLogs;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: "min-w-96 min-h-64" }}
    >
      <DialogTitle className="flex justify-between items-center">
        {t("views.fleet.robots.config.auditLog.title")}
        <IconButton onClick={onClose}>
          <CloseIcon className="text-gray-400" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="relative m-0">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <CircularProgress classes={{ circle: "text-white" }} />
          </div>
        )}
        {logs && (
          <ol className="pl-0 flex flex-col gap-4">
            {logs.map((log, i) => (
              <Logline
                key={
                  i // there aren't fields that are guaranteed to be unique */
                }
                log={log}
              />
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ConfigAuditLog = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.configs,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.configs,
        PermissionDomain.all
      ),
    ],
  },
  _ConfigAuditLog
);

interface LoglineProps {
  log: RosyAuditLog;
}

const Logline: FunctionComponent<LoglineProps> = ({ log }) => {
  const { t, i18n } = useTranslation();
  const { userId, createTime, oldValue, newValue } = log;
  // RoSy sometimes gives us a `userId` that's actually just a robot serial.
  // Real user IDs should look like `auth0|12345` or `google-oauth2|12345` or
  // similar, not `slayer1`. Remove hack when RoSy always returns user IDs.
  const isSerial = !userId.includes("|");
  const hasUser = userId !== "" && !isSerial;
  const { data } = useQueryPopups(
    userApi.endpoints.getUser.useQuery(hasUser ? { userId } : skipToken)
  );
  const displayName = isSerial ? userId : data?.user?.name ?? data?.user?.email;

  return (
    <li className="p-0">
      <div className="flex gap-2 items-center text-xs">
        <Chip
          size="small"
          variant="filled"
          className="text-inherit"
          avatar={<Avatar alt={displayName} src={data?.user?.picture} />}
          label={displayName ?? t("components.Loading.placeholder")}
        />
        <span>
          {createTime &&
            DateTime.fromISO(createTime).toLocaleString(
              DateTime.DATETIME_SHORT,
              { locale: i18n.language }
            )}
        </span>
      </div>
      <div className="flex gap-2 items-center font-mono text-sm">
        <div className="line-through">
          <ConfigValueView value={oldValue} />
        </div>
        <ChangeIcon />
        <div>
          <ConfigValueView value={newValue} />
        </div>
      </div>
      <div>{`${t("views.admin.config.reason")}: ${log.reason}`}</div>
    </li>
  );
};
